import residentialDevelopment from '@/assets/img/users-cases/3d-digital-agency-residential-development.webp';
import buildingFloorSelection from '@/assets/img/users-cases/building-floor-apartment-selection.webp';
import aiWorkflow from '@/assets/img/users-cases/floor-plan-ai-2d-to-3d-workflow.webp';
import furnishedFloorPlan from '@/assets/img/users-cases/furnished-3d-floor-plan-real-estate.webp';
import apartmentTour from '@/assets/img/users-cases/360-apartment-tour-interior-visualization.webp';

const accent = (text) => `<span style="color: #FFA900">${text}</span>`;

export default {
  slug: '3d-digital-agency-39-apartment-tours',
  order: 0,
  title: '39 Apartment Tours Without Expanding the 3D Production Team',
  subtitle: 'See how a 3D digital agency used Getfloorplan to scale apartment visualization and create 39 furnished 3D spaces and 360° tours without expanding manual production.',
  metaTitle: '3D Digital Agency Case: 39 Apartment Tours | Getfloorplan',
  metaDescription: 'See how a 3D digital agency used Getfloorplan AI to turn apartment floor plans into furnished 3D visuals and 39 interactive tours with one scalable workflow.',
  tags: [],

  images: {
    hero: residentialDevelopment,
  },
  heroImageAlt: 'Residential development visualization for a 3D digital agency apartment tour project',

  heroText: [
    `A 3D digital agency needed to produce ${accent('39 apartment tours')} for a residential development.`,
    `${accent('Getfloorplan AI')} turned apartment layouts into furnished ${accent('3D floor plans')} and ${accent('360° virtual tours')}, helping the agency scale production without manually modeling every unit.`,
  ],

  sections: [
    {
      title: 'The Challenge: Visualizing 39 Apartments at Scale',
      image: buildingFloorSelection,
      imageAlt: 'Building floor selection connecting a residential development to individual apartment visualization',
      paragraphs: [
        'The agency had already created the exterior visuals and digital presentation for a residential development.',
        'The next step was to show buyers the apartments themselves — their space, interiors and potential.',
        'The challenge was scale: 39 apartments had to be visualized fast, while manual production would require significant 3D resources.',
        'That’s where Getfloorplan AI became part of the workflow.',
      ],
    },
    {
      title: 'How Getfloorplan AI Turned Floor Plans Into 3D Apartment Tours',
      image: aiWorkflow,
      imageAlt: 'Floor plan AI workflow transforming a 2D apartment layout into a furnished 3D floor plan rendering',
      paragraphs: [
        'The agency provided apartment layouts, and Getfloorplan turned them into furnished 3D floor plans and virtual-tour-ready experiences using a scalable AI workflow.',
        'Unlike technical plans showing walls and dimensions, furnished 3D floor plans make properties easier to understand through room relationships, furniture placement and spatial potential.',
        'For agencies working with multiple units, scalable <a href="https://getfloorplan.com">3D floor plan services</a> help reduce repetitive production while maintaining a consistent visual result.',
        'Workflow: Floor plan → Floor planner → 3D floor plan → 360° virtual tour',
      ],
    },
    {
      title: 'From the development to a specific apartment',
      image: furnishedFloorPlan,
      imageAlt: 'Furnished 3D floor plan showing the layout and interior potential of an apartment',
      paragraphs: [
        'The apartment visuals became part of the wider project experience.',
        'Buyers could move naturally through:',
        'Development → Building → Floor → Apartment → 360° Tour',
        'This connected the agency’s exterior presentation with the apartment-level experience created through Getfloorplan.',
      ],
    },
    {
      title: 'The Result: 39 Apartment Tours From One Scalable Workflow',
      image: apartmentTour,
      imageAlt: 'Interior views from a 360 apartment tour created for a residential development',
      paragraphs: [
        `The agency produced ${accent('39 apartment tours')} using one scalable ${accent('Getfloorplan AI')} workflow instead of treating each apartment as a separate manual 3D modeling project.`,
      ],
      list: [
        '39 Apartment tours',
        '3D + 360° – apartment visualization',
        'Scalable production – without manual modeling',
      ],
      afterListParagraphs: [
        'The agency stayed focused on architecture and creative visualization, while Getfloorplan supported repetitive apartment production.',
      ],
      cta: {
        text: 'View the 360 tour',
        href: 'https://base.getfloorplan.com/?crmPlanId=490032',
      },
    },
    {
      title: 'Scale Apartment Visualization for Your Next Project',
      titleParagraphs: [
        'Need to produce 10, 20, 40 or more apartments?',
      ],
      paragraphs: [
        'Use Getfloorplan as your production partner for floor plans, 3D floor plan renderings and 360° virtual tours.',
      ],
      cta: {
        text: 'Create Your Floor Plan',
        href: '/pricing',
      },
    },
  ],

  related: [
    '3D-tour-american-flat',
    'residential-property-marketing-campaigns',
    'smart-space-apartment',
    'boho-harmony-apartment',
  ],
};
